import { useEffect, useRef, useState } from 'react';
import type { AttendanceRow } from '../../models/attendance.types';
import { shiftWorkforceService } from '../../services/workforce/shift-workforce.service';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { dateKey, initialFrom } from './attendance-page.utils';

export function useAttendancePageController() {
  const { user } = useShiftAuth();

  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(() => dateKey(new Date()));
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageLock = useRef(false);
  const generation = useRef(0);
  const liveRows = useRef(new Map<number, AttendanceRow | null>());

  const mergeAttendance = (rows: AttendanceRow[], changes: Map<number, AttendanceRow | null>) => {
    const merged = new Map(rows.map((row) => [row.id, row]));
    for (const [id, row] of changes) {
      if (row && row.date >= from && row.date <= to) merged.set(id, row);
      else merged.delete(id);
    }
    return [...merged.values()].sort((left, right) => right.date.localeCompare(left.date)
      || new Date(right.startShiftAt).getTime() - new Date(left.startShiftAt).getTime() || right.id - left.id);
  };

  const loadAttendance = async (append = false) => {
    if (append && pageLock.current) return;
    pageLock.current = true;
    liveRows.current.clear();
    const requestGeneration = ++generation.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError(null);
    try {
      const result = await shiftWorkforceService.getAttendance(from, to, { offset: append ? rows.length : 0, limit: 50 });
      if (requestGeneration !== generation.current) return;
      const changes = new Map(liveRows.current);
      liveRows.current.clear();
      setRows((current) => mergeAttendance(append ? [...current, ...result.attendance.filter((row) => !current.some((existing) => existing.id === row.id))] : result.attendance, changes));
      setHasMore(result.pagination?.hasMore ?? false);
    } catch {
      if (requestGeneration === generation.current) setError('לא ניתן לטעון את נתוני הנוכחות.');
    } finally {
      if (requestGeneration === generation.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
        pageLock.current = false;
      }
    }
  };

  const eventsReady = useShiftEvents((event) => {
    if (event.type === 'attendance.upserted') {
      if (pageLock.current) liveRows.current.set(event.attendance.id, event.attendance);
      setRows((current) => mergeAttendance(current, new Map([[event.attendance.id, event.attendance]])));
    } else if (event.type === 'attendance.removed') {
      if (pageLock.current) liveRows.current.set(event.id, null);
      setRows((current) => current.filter((row) => row.id !== event.id));
    } else if (event.type === 'sse.resync') void loadAttendance();
  });

  useEffect(() => {
    if (eventsReady) void loadAttendance();
    return () => { generation.current += 1; };
  }, [from, to, eventsReady]);

  return {
    user,
    from,
    setFrom,
    to,
    setTo,
    rows,
    isLoading,
    hasMore,
    isLoadingMore,
    onLoadMore: () => loadAttendance(true),
    error,
  };
}
